import type { Nullable } from "core/types";
import { Observable } from "core/Misc/observable";
import type { Vector2 } from "core/Maths/math.vector";

import { Rectangle } from "./rectangle";
import type { Control } from "./control";
import { RegisterClass } from "core/Misc/typeStore";
import type { PointerInfoBase } from "core/Events/pointerEvents";

/**
 * Class used to create toggle buttons
 * @since 5.0.0
 */
export class ToggleButton extends Rectangle {
    /**
     * Function called to generate the toActive animation
     */
    public toActiveAnimation: () => void;

    /**
     * Function called to generate the toInactive animation
     */
    public toInactiveAnimation: () => void;

    /**
     * Function called to generate a pointer enter animation when the toggle button is active.
     */
    public pointerEnterActiveAnimation: () => void;
    /**
     * Function called to generate a pointer out animation when the toggle button is active.
     */
    public pointerOutActiveAnimation: () => void;
    /**
     * Function called to generate a pointer down animation when the toggle button is active.
     */
    public pointerDownActiveAnimation: () => void;
    /**
     * Function called to generate a pointer up animation when the toggle button is active.
     */
    public pointerUpActiveAnimation: () => void;

    /**
     * Function called to generate a pointer enter animation when the toggle button is inactive.
     */
    public pointerEnterInactiveAnimation: () => void;
    /**
     * Function called to generate a pointer out animation when the toggle button is inactive.
     */
    public pointerOutInactiveAnimation: () => void;
    /**
     * Function called to generate a pointer down animation when the toggle button is inactive.
     */
    public pointerDownInactiveAnimation: () => void;
    /**
     * Function called to generate a pointer up animation when the toggle button is inactive.
     */
    public pointerUpInactiveAnimation: () => void;

    /** Observable raised when isActive is changed */
    public onIsActiveChangedObservable = new Observable<boolean>();

    /**
     * Gets or sets a boolean indicating that the toggle button will let internal controls handle picking instead of doing it directly using its bounding info
     */
    public delegatePickingToChildren = false;

    private _group: string;
    /** Gets or sets group name this toggle button belongs to */
    public get group(): string {
        return this._group;
    }
    public set group(value: string) {
        if (this._group === value) {
            return;
        }

        this._group = value;
    }

    private _isActive = false;
    /** Gets or sets a boolean indicating if the toggle button is active or not */
    public get isActive(): boolean {
        return this._isActive;
    }
    public set isActive(value: boolean) {
        // Function modeled after radioButton.ts
        if (this._isActive === value) {
            return;
        }

        this._isActive = value;

        // Update the visual state based on the new value
        if (this._isActive) {
            this.toActiveAnimation?.();
        } else {
            this.toInactiveAnimation?.();
        }

        this._markAsDirty();

        this.onIsActiveChangedObservable.notifyObservers(value);

        if (this._isActive && this._host && this._group) {
            // A toggle button in a group can only have 1 active element at a given time.
            // If this toggle button has a group, set other toggle buttons in the group to inactive.
            this._host.executeOnAllControls((control) => {
                // Check for control type ToggleButton
                if (control.typeName === "ToggleButton") {
                    // Don't do anything to this toggle button
                    if (control === this) {
                        return;
                    }

                    const childToggle = <ToggleButton>control;
                    // If toggle button is in same group, set isActive to false
                    if (childToggle.group === this.group) {
                        childToggle.isActive = false;
                    }
                }
            });
        }
    }

    /**
     * Creates a new ToggleButton
     * @param name defines the control name
     * @param group defines the toggle group this toggle belongs to
     */
    constructor(public name?: string, group?: string) {
        super(name);
        this.group = group ?? "";

        this.thickness = 0;
        this.isPointerBlocker = true;

        let alphaStore: Nullable<number> = null;

        this.toActiveAnimation = () => {
            this.thickness = 1;
        };
        this.toInactiveAnimation = () => {
            this.thickness = 0;
        };

        this.pointerEnterActiveAnimation = () => {
            alphaStore = this.alpha;
            this.alpha -= 0.1;
        };

        this.pointerOutActiveAnimation = () => {
            if (alphaStore !== null) {
                this.alpha = alphaStore;
            }
        };

        this.pointerDownActiveAnimation = () => {
            this.scaleX -= 0.05;
            this.scaleY -= 0.05;
        };

        this.pointerUpActiveAnimation = () => {
            this.scaleX += 0.05;
            this.scaleY += 0.05;
        };

        this.pointerEnterInactiveAnimation = () => {
            alphaStore = this.alpha;
            this.alpha -= 0.1;
        };

        this.pointerOutInactiveAnimation = () => {
            if (alphaStore !== null) {
                this.alpha = alphaStore;
            }
        };

        this.pointerDownInactiveAnimation = () => {
            this.scaleX -= 0.05;
            this.scaleY -= 0.05;
        };

        this.pointerUpInactiveAnimation = () => {
            this.scaleX += 0.05;
            this.scaleY += 0.05;
        };
    }

    protected _getTypeName(): string {
        return "ToggleButton";
    }

    // While being a container, the toggle button behaves like a control.
    /**
     * @param x
     * @param y
     * @param pi
     * @param type
     * @param pointerId
     * @param buttonIndex
     * @param deltaX
     * @param deltaY
     * @hidden
     */
    public _processPicking(x: number, y: number, pi: PointerInfoBase, type: number, pointerId: number, buttonIndex: number, deltaX?: number, deltaY?: number): boolean {
        if (!this._isEnabled || !this.isHitTestVisible || !this.isVisible || this.notRenderable) {
            return false;
        }

        if (!super.contains(x, y)) {
            return false;
        }

        if (this.delegatePickingToChildren) {
            let contains = false;
            for (let index = this._children.length - 1; index >= 0; index--) {
                const child = this._children[index];
                if (child.isEnabled && child.isHitTestVisible && child.isVisible && !child.notRenderable && child.contains(x, y)) {
                    contains = true;
                    break;
                }
            }

            if (!contains) {
                return false;
            }
        }

        this._processObservables(type, x, y, pi, pointerId, buttonIndex, deltaX, deltaY);

        return true;
    }

    /**
     * @param target
     * @param pi
     * @hidden
     */
    public _onPointerEnter(target: Control, pi: PointerInfoBase): boolean {
        if (!super._onPointerEnter(target, pi)) {
            return false;
        }

        if (this.isReadOnly) {
            return true;
        }

        if (this._isActive) {
            if (this.pointerEnterActiveAnimation) {
                this.pointerEnterActiveAnimation();
            }
        } else {
            if (this.pointerEnterInactiveAnimation) {
                this.pointerEnterInactiveAnimation();
            }
        }

        return true;
    }

    /**
     * @param target
     * @param pi
     * @param force
     * @hidden
     */
    public _onPointerOut(target: Control, pi: PointerInfoBase, force = false): void {
        if (!this.isReadOnly) {
            if (this._isActive) {
                if (this.pointerOutActiveAnimation) {
                    this.pointerOutActiveAnimation();
                }
            } else {
                if (this.pointerOutInactiveAnimation) {
                    this.pointerOutInactiveAnimation();
                }
            }
        }

        super._onPointerOut(target, pi, force);
    }

    /**
     * @param target
     * @param coordinates
     * @param pointerId
     * @param buttonIndex
     * @param pi
     * @hidden
     */
    public _onPointerDown(target: Control, coordinates: Vector2, pointerId: number, buttonIndex: number, pi: PointerInfoBase): boolean {
        if (!super._onPointerDown(target, coordinates, pointerId, buttonIndex, pi)) {
            return false;
        }

        if (this.isReadOnly) {
            return true;
        }

        if (this._isActive) {
            if (this.pointerDownActiveAnimation) {
                this.pointerDownActiveAnimation();
            }
        } else {
            if (this.pointerDownInactiveAnimation) {
                this.pointerDownInactiveAnimation();
            }
        }

        return true;
    }

    /**
     * @param target
     * @param coordinates
     * @param pointerId
     * @param buttonIndex
     * @param notifyClick
     * @param pi
     * @hidden
     */
    public _onPointerUp(target: Control, coordinates: Vector2, pointerId: number, buttonIndex: number, notifyClick: boolean, pi: PointerInfoBase): void {
        if (!this.isReadOnly) {
            if (this._isActive) {
                if (this.pointerUpActiveAnimation) {
                    this.pointerUpActiveAnimation();
                }
            } else {
                if (this.pointerUpInactiveAnimation) {
                    this.pointerUpInactiveAnimation();
                }
            }
        }

        super._onPointerUp(target, coordinates, pointerId, buttonIndex, notifyClick, pi);
    }
}

RegisterClass("BABYLON.GUI.ToggleButton", ToggleButton);
